import { Box, Checkbox, Divider, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import { type ChangeEvent, type FC } from "react"
import type { UseFormReturn } from "react-hook-form"

import { WAREHOUSE_SHELVING_BIN_STATUS } from "../constants"
import { getWarehouseShelvingActiveBinStatusKey } from "../utils"

import { Button } from "@/components/common/Button"
import { StatusFilter } from "@/components/common/Filters"
import { BIN_SIZE_TO_LABEL } from "@/constants/warehouse"
import { type WarehouseShelvingListFilter } from "@/graphql/codegen/graphql"

interface WarehouseShelvingFilterProps {
  changeFilter: (name: string, value: any) => void
  formMethods: UseFormReturn<WarehouseShelvingListFilter, any, undefined>
  clearBinStatus: () => void
  enableFilterIfChecked: (name: string, checked: boolean | null | undefined) => void
  changeBinStatus: (status: string) => void
}

export const WarehouseShelvingFilter: FC<WarehouseShelvingFilterProps> = ({
  changeFilter,
  formMethods,
  clearBinStatus,
  enableFilterIfChecked,
  changeBinStatus,
}) => {
  const values = formMethods.getValues()

  const binStatus = getWarehouseShelvingActiveBinStatusKey(values.isAvailable, values.isDoNotUse)

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    // remove this field from search params if value False
    enableFilterIfChecked(name, checked)
  }

  const handleBinStatusChange = (e: ChangeEvent<HTMLInputElement>) => {
    const clickedValue = e.target.value
    changeBinStatus(clickedValue)
  }

  return (
    <Box display='flex' flexDirection='column' gap={4} data-test='warehouseShelvingFilter'>
      <Box>
        <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
          Shelving Type
        </Typography>
        <Divider />

        <Box display='flex' flexDirection='column' className='px-8 py-2'>
          <FormControlLabel
            control={<Checkbox name='coldStorage' checked={!!values.coldStorage} onChange={handleCheckboxChange} />}
            label='Cold Storage'
          />
          <FormControlLabel
            control={<Checkbox name='h3ColdStorage' checked={!!values.h3ColdStorage} onChange={handleCheckboxChange} />}
            label='H3 Storage'
          />
          <FormControlLabel
            control={<Checkbox name='deep' checked={!!values.deep} onChange={handleCheckboxChange} />}
            label='Deep'
          />
        </Box>
      </Box>
      <Box>
        <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
          Bin Size
        </Typography>
        <Divider />

        <Box>
          <StatusFilter
            name='binSize'
            data={Object.keys(BIN_SIZE_TO_LABEL)}
            control={formMethods.control}
            onFilterChange={changeFilter}
            labelMap={BIN_SIZE_TO_LABEL}
          />
        </Box>
      </Box>
      <Box>
        <Box display='flex' justifyContent={"space-between"} className='pr-4'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Bin Status
          </Typography>

          <Button
            variant='primary'
            size='small'
            role='button'
            appearance='text'
            startIcon={<DeleteOutlined fontSize='small' />}
            onClick={clearBinStatus}
            disabled={binStatus === null}
          >
            Clear
          </Button>
        </Box>

        <Divider />
        <Box display='flex' flexDirection='column' className='px-8 py-2'>
          <RadioGroup name='binStatus' value={binStatus} className='gap-0.5' onChange={handleBinStatusChange}>
            <FormControlLabel value={WAREHOUSE_SHELVING_BIN_STATUS.AVAILABLE} control={<Radio />} label='Available' />
            <FormControlLabel
              value={WAREHOUSE_SHELVING_BIN_STATUS.UNAVAILABLE}
              control={<Radio />}
              label='Unavailable'
            />
            <FormControlLabel value={WAREHOUSE_SHELVING_BIN_STATUS.DO_NOT_USE} control={<Radio />} label='Do Not Use' />
          </RadioGroup>
        </Box>
      </Box>
      <Box>
        <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
          General
        </Typography>
        <Divider />

        <Box display='flex' flexDirection='column' className='px-8 py-2'>
          <FormControlLabel
            control={<Checkbox name='isUndefined' checked={!!values.isUndefined} onChange={handleCheckboxChange} />}
            label='Undefined'
          />
        </Box>
      </Box>
    </Box>
  )
}
